import { Instance, flow, getParent, types } from 'mobx-state-tree';

import { sendToken } from '@common/api/notifications';
import { vapidKey } from '@common/constants/firebase';
import { IStoreInterfaceModel } from '@common/models/StoreInterface';

const SERVICE_WORKER_READY_TIMEOUT_MS = 8000;
const SUBSCRIBE_TIMEOUT_MS = 8000;
const SUBSCRIPTION_RETRY_INTERVAL_MS = 30000;

type SubscriptionSyncResult = {
	subscriptionText: string;
	sent: boolean;
};

let permissionRequestPromise: Promise<NotificationPermission> | null = null;
let subscriptionSyncPromise: Promise<SubscriptionSyncResult | null> | null = null;
let subscriptionRetryTimeout: ReturnType<typeof setTimeout> | null = null;

function urlBase64ToUint8Array(base64String: string) {
	const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
	const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
	const rawData = window.atob(base64);
	const outputArray = new Uint8Array(rawData.length);
	for (let i = 0; i < rawData.length; i += 1) {
		outputArray[i] = rawData.charCodeAt(i);
	}
	return outputArray;
}

async function resolveServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | undefined> {
	if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
		return undefined;
	}

	try {
		const existing = (await navigator.serviceWorker.getRegistration()) as
			| ServiceWorkerRegistration
			| undefined;
		if (existing) return existing;
	} catch (error) {
		console.error('[Push] getRegistration error', error);
	}

	try {
		const registration = (await Promise.race([
			navigator.serviceWorker.ready,
			new Promise<never>((_, reject) => {
				setTimeout(
					() => reject(new Error('serviceWorker.ready timeout')),
					SERVICE_WORKER_READY_TIMEOUT_MS,
				);
			}),
		])) as ServiceWorkerRegistration;

		return registration;
	} catch (error) {
		console.error('[Push] serviceWorker.ready error', error);
		return undefined;
	}
}

async function ensureSubscription(
	registration: ServiceWorkerRegistration,
): Promise<PushSubscription | null> {
	if (!registration.pushManager) return null;

	const existing = await registration.pushManager.getSubscription();
	if (existing) return existing;

	if (!vapidKey) {
		console.info('[Push] no VAPID key');
		return null;
	}

	const appServerKey = urlBase64ToUint8Array(vapidKey);
	const subscription = await Promise.race([
		registration.pushManager.subscribe({
			userVisibleOnly: true,
			applicationServerKey: appServerKey,
		}),
		new Promise<never>((_, reject) => {
			setTimeout(() => reject(new Error('subscribe timeout')), SUBSCRIBE_TIMEOUT_MS);
		}),
	]);

	return subscription;
}

const PushNotifications = types.model('PushNotifications', {}).actions((self) => {
	let syncTokenIfNeeded: (args?: { reason?: string }) => any;

	function scheduleSubscriptionRetry() {
		if (subscriptionRetryTimeout) return;
		subscriptionRetryTimeout = setTimeout(() => {
			subscriptionRetryTimeout = null;
			if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
				return;
			}
			syncTokenIfNeeded({ reason: 'retry' });
		}, SUBSCRIPTION_RETRY_INTERVAL_MS);
	}

	syncTokenIfNeeded = flow(function* ({
		reason = 'manual',
	}: {
		reason?: string;
	} = {}) {
		if (typeof window === 'undefined' || typeof Notification === 'undefined') return null;
		if (Notification.permission !== 'granted') return null;

		if (subscriptionSyncPromise) {
			const result = (yield subscriptionSyncPromise) as SubscriptionSyncResult | null;
			return result;
		}

		subscriptionSyncPromise = (async () => {
			try {
				const registration = await resolveServiceWorkerRegistration();
				if (!registration) {
					console.info('[Push] no service worker registration');
					return null;
				}

				const subscription = await ensureSubscription(registration);
				if (!subscription) {
					console.info('[Push] no push subscription');
					return null;
				}

				const tokenPayload = subscription.toJSON();
				const subscriptionText = JSON.stringify(tokenPayload, null, 2);
				const store = getParent(self) as IStoreInterfaceModel;
				const isAuthorized = store.auth?.isAuthorized ?? true;

				let sent = false;
				if (isAuthorized) {
					await sendToken(tokenPayload);
					sent = true;
				}

				console.info('[Push] subscription synced', { reason });
				return { subscriptionText, sent };
			} catch (error) {
				console.error('[Push] subscription sync error', error);
				return null;
			} finally {
				subscriptionSyncPromise = null;
			}
		})();

		const result = (yield subscriptionSyncPromise) as SubscriptionSyncResult | null;
		if (result && subscriptionRetryTimeout) {
			clearTimeout(subscriptionRetryTimeout);
			subscriptionRetryTimeout = null;
		}
		if (!result && Notification.permission === 'granted') {
			scheduleSubscriptionRetry();
		}
		return result;
	});

	const requestPermissions = flow(function* () {
		if (typeof window === 'undefined' || typeof Notification === 'undefined') {
			console.info('[Push] no window or Notification');
			return 'denied' as NotificationPermission;
		}

		if (permissionRequestPromise) {
			return (yield permissionRequestPromise) as NotificationPermission;
		}

		permissionRequestPromise = (async () => {
			const currentPermission = Notification.permission;
			const permission: NotificationPermission =
				currentPermission === 'default'
					? ((await Notification.requestPermission()) as NotificationPermission)
					: currentPermission;

			if (permission !== 'granted') return permission;

			try {
				await syncTokenIfNeeded({
					reason: currentPermission === 'default' ? 'permission' : 'fetch',
				});
			} catch (error) {
				console.error('[Push] sync token error', error);
			}

			return permission;
		})();

		try {
			return (yield permissionRequestPromise) as NotificationPermission;
		} finally {
			permissionRequestPromise = null;
		}
	});

	return {
		scheduleSubscriptionRetry,
		syncTokenIfNeeded,
		requestPermissions,
	};
});

export default PushNotifications;

export interface IPushNotificationsModel extends Instance<typeof PushNotifications> {}
